/**
 * Sentry Initialization
 *
 * Self-contained init module. Called once from src/index.js on app startup.
 * If Sentry init fails for any reason, app continues normally (silent failure).
 *
 * Features enabled:
 *  - Error monitoring (auto-captures uncaught errors + unhandled rejections)
 *  - Session Replay (records user session — privacy-safe defaults)
 *  - Browser tracing (performance)
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = 'https://4efcd29de271119624ad64ab0508b5bb@o4511494484393984.ingest.us.sentry.io/4511494497763328';

/**
 * Initialize Sentry — call once at app startup
 * Safe to call multiple times (Sentry handles double-init internally)
 */
export const initSentry = () => {
  try {
    // Skip in local dev unless explicitly enabled
    const isProduction = process.env.NODE_ENV === 'production';
    const forceEnable = process.env.REACT_APP_SENTRY_ENABLE === 'true';

    if (!isProduction && !forceEnable) {
      // Don't init in local dev to avoid noise — set REACT_APP_SENTRY_ENABLE=true to enable
      return;
    }

    Sentry.init({
      dsn: SENTRY_DSN,

      // Auto-instruments React Router, fetch, console etc.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Privacy-safe defaults — mask all text + media so no PII leaks
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in dev

      // Session Replay sample rates
      // Always record errors; for normal sessions, record only a small %
      replaysSessionSampleRate: 0.05, // 5% of normal sessions
      replaysOnErrorSampleRate: 1.0,  // 100% of sessions that hit an error

      // Environment + release for filtering errors in Sentry dashboard
      environment: isProduction ? 'production' : 'development',
      release: process.env.REACT_APP_VERSION || 'booking-engine@unknown',

      // Don't spam dashboard with known noisy errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        // Random plugins/extensions
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        // Generic browser quirks
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Network errors that aren't our app's fault
        'Network request failed',
        'NetworkError',
        'Failed to fetch',
        // Old browsers
        'Non-Error promise rejection captured',
      ],

      // Filter out errors from third-party scripts (chatbots, analytics, etc.)
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        // Common third-party scripts
        /googletagmanager\.com/,
        /google-analytics\.com/,
        /clarity\.ms/,
        /facebook\.net/,
      ],

      // Before sending — last chance to filter/scrub
      beforeSend(event) {
        // Drop events without exceptions (rare, but reduces noise)
        if (!event.exception) return event;
        return event;
      },
    });
  } catch (err) {
    // Never let Sentry init break the app
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[Sentry] Init failed — continuing without error tracking:', err && err.message);
    }
  }
};

/**
 * Re-export Sentry for use in ErrorBoundary or manual capture
 */
export { Sentry };
